import QuestionSettingsCard from 'components/CreateQuiz/QuestionSettingsCard';
import QuestionSettingsNavigation from 'components/CreateQuiz/QuestionSettingsNavigation';
import { PageContext } from 'pages/CreateQuiz/PageProvider';
import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, styled, Text } from 'shell/components';
import { QuizRubricType } from 'utils/quizRubric';

const QuestionSettingsStep = ({ appearance = 'create', device }) => {
    const { t } = useTranslation('quiz');
    const { overview, form, isResyncing, handleCriterionIdsChange, handleGradingNotesChange, handleResyncQuiz } =
        useContext(PageContext);
    const questionRefs = useRef({});
    const isEdit = appearance === 'edit';
    const isMobile = device === 'mobile';
    const isQuestionRubric = form.rubricType === QuizRubricType.QUESTION;
    const questions = overview.timelyQuestions;

    const showQuestion = (questionId) => {
        questionRefs.current[questionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        questionRefs.current[questionId]?.focus({ preventScroll: true });
    };

    return (
        <QuestionLayout direction={isMobile ? 'column' : 'row'} $isEdit={isEdit}>
            {!isEdit && (
                <QuestionSettingsNavigation
                    device={device}
                    questions={questions}
                    isResyncing={isResyncing}
                    onResync={handleResyncQuiz}
                    onSelect={showQuestion}
                />
            )}
            <QuestionContent component={'main'} spacing={5} $device={device} $isEdit={isEdit}>
                {questions.length === 0 ? (
                    <EmptyQuestions>
                        <Text>{t('create.no-written-questions', 'This quiz has no written-response questions.')}</Text>
                    </EmptyQuestions>
                ) : (
                    questions.map((question, index) => (
                        <QuestionSettingsCard
                            key={question.id}
                            ref={(node) => {
                                questionRefs.current[question.id] = node;
                            }}
                            question={question}
                            questionNumber={index + 1}
                            showQuestionNumber={isEdit && isQuestionRubric}
                            isMobile={isMobile}
                            isQuestionRubric={isQuestionRubric}
                            criteria={form.rubric?.criteria || []}
                            onCriterionIdsChange={(criterionIds) => handleCriterionIdsChange(question.id, criterionIds)}
                            onGradingNotesChange={(notes) => handleGradingNotesChange(question.id, notes)}
                        />
                    ))
                )}
            </QuestionContent>
        </QuestionLayout>
    );
};

const QuestionLayout = styled(Stack, {
    shouldForwardProp: (prop) => prop !== '$isEdit',
})(({ $isEdit, theme }) => ({
    flex: 1,
    minHeight: 0,
    background: $isEdit ? theme.palette.ensightful.shades[0] : theme.palette.ensightful.neutral[50],
}));

const QuestionContent = styled(Stack, {
    shouldForwardProp: (prop) => !['$device', '$isEdit'].includes(prop),
})(({ $device, $isEdit, theme }) => ({
    width: $isEdit || $device === 'mobile' ? '100%' : 'auto',
    minWidth: 0,
    flex: 1,
    padding: $isEdit ? theme.spacing(5, $device === 'mobile' ? 4 : 8) : theme.spacing($device === 'mobile' ? 4 : 5),
}));

const EmptyQuestions = styled(Box)(({ theme }) => ({
    padding: theme.spacing(5),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
    background: theme.palette.ensightful.shades[0],
}));

export default QuestionSettingsStep;
